import { ReactNode } from 'react';
import { ORDERS_HEADER } from './appConstants';

export const navItems = [
  {
    key: 'nav-route-item-region-map',
    label: 'Region Map',
    icon: 'map',
    navRoute: '/map',
    dataTest: 'RegionMap',
  },
  {
    key: 'nav-route-item-docs',
    label: 'Docs',
    icon: 'docs',
    navRoute: '/docs',
    dataTest: 'Docs',
  },
];

export const VEHICLES_TEXT = 'Vehicles';
export const TRUCKS_TEXT = 'Trucks';
export const VEHICLE_TYPES_TEXT = 'Vehicle Types';
export const FLAGS_TEXT = 'Feature Flags';
export const DRIVERS_TEXT = 'Drivers';
export const PLANTS_TEXT = 'Plants';
export const DEVICES_TEXT = 'Devices';
export const HAULERS_TEXT = 'Haulers';
export const SCALES_TEXT = 'Scales';
export const TICKETS_TEXT = 'Tickets';
export const GEOZONES_TEXT = 'Geozones';
export const REGIONS_TEXT = 'Regions';
export const CHECKLISTS_TEXT = 'Checklists';
export const EMAIL_NOTIFICATIONS_TEXT = 'Email Notifications';
export const USERS_TEXT = 'Users';
export const PERMISSIONS_TEXT = 'Security Permissions';
export const GROUP_PERMISSIONS_TEXT = 'Security Permissions Groups';
export const USER_PERMISSIONS_TEXT = 'Security Permissions Users';
export const OPEN_TICKETS_TEXT = 'Open Tickets';
export const GLINX_DEVICE_ROUTE = '/admin/devices/glinx/';
export const TABLET_DEVICE_ROUTE = '/admin/devices/';

export interface AdminCategory {
  key: string;
  name: string;
  icon?: string;
  navRoute?: string;
  companyWide: boolean;
  subCategories?: AdminCategory[];
}

export interface NavItem {
  key: string;
  label?: string;
  icon?: string;
  navRoute?: string;
  dataTest?: string;
  /** Optional badge rendered inside the nav route button (e.g. a notification dot). */
  element?: ReactNode;
}

export const categoriesForAdmin: AdminCategory[] = [
  {
    key: 'admin-category-drivers',
    name: DRIVERS_TEXT,
    icon: 'icon-wheel',
    navRoute: '/admin/drivers',
    companyWide: false,
  },
  {
    key: 'admin-category-users',
    name: USERS_TEXT,
    icon: 'icon-headset-mic',
    navRoute: '/admin/users',
    companyWide: false,
  },
  {
    key: 'admin-category-trucks',
    name: VEHICLES_TEXT,
    icon: 'icon-truck-dump',
    companyWide: false,
    navRoute: '/admin/trucks',
    subCategories: [
      {
        key: 'admin-category-trucks-list',
        name: VEHICLES_TEXT,
        navRoute: '/admin/trucks',
        companyWide: false,
      },
      {
        key: 'admin-category-vehicle-types-list',
        name: VEHICLE_TYPES_TEXT,
        navRoute: '/admin/vehicletypes',
        companyWide: false,
      },
    ],
  },
  {
    key: 'admin-category-plants',
    name: PLANTS_TEXT,
    icon: 'icon-flag',
    navRoute: '/admin/plants',
    companyWide: false,
  },
  {
    key: 'admin-category-scales',
    name: SCALES_TEXT,
    icon: 'icon-scale',
    navRoute: '/admin/scales',
    companyWide: false,
  },
  {
    key: 'admin-category-devices',
    name: DEVICES_TEXT,
    icon: 'icon-tablet',
    navRoute: '/admin/devices',
    companyWide: false,
  },
  {
    key: 'admin-category-haulers',
    name: HAULERS_TEXT,
    icon: 'icon-people',
    navRoute: '/admin/haulers',
    companyWide: false,
  },
  {
    key: 'admin-category-geozones',
    name: GEOZONES_TEXT,
    icon: 'icon-zones',
    navRoute: '/admin/geozones',
    companyWide: false,
  },
  {
    key: 'admin-category-regions',
    name: REGIONS_TEXT,
    icon: 'icon-map',
    navRoute: '/admin/regions',
    companyWide: true,
  },
  {
    key: 'admin-category-checklists',
    name: CHECKLISTS_TEXT,
    icon: 'icon-check-box-outline',
    navRoute: '/admin/checklists',
    companyWide: true,
  },
  {
    key: 'admin-category-email-notifications',
    name: EMAIL_NOTIFICATIONS_TEXT,
    icon: 'icon-mail',
    navRoute: '/admin/emailnotifications',
    companyWide: true,
  },
  {
    key: 'admin-category-open-tickets',
    name: OPEN_TICKETS_TEXT,
    icon: 'icon-tickets',
    navRoute: '/admin/tickets',
    companyWide: true,
  },
  {
    key: 'admin-category-permissions',
    name: PERMISSIONS_TEXT,
    icon: 'icon-lock-solid',
    navRoute: '/admin/permissions',
    companyWide: true,
  },
  {
    key: 'admin-category-feature-flags',
    name: FLAGS_TEXT,
    icon: 'icon-toggle',
    navRoute: '/admin/featureflags',
    companyWide: true,
  },
];

/**
 * {@link https://trucktrax.atlassian.net/browse/DEV-2} introduced a change where vehicles and vehicle types
 * are separate nav items. However, this change is intended to be
 * used in conjunction with the feature flag function: @see {@link isExternalVehicleAddEditEnabled}
 */
export const featureFlagVehicleNavItem = {
  index: 2,
  itemsOffset: 1,
  items: [
    {
      key: 'feature-flag-vehicles',
      name: VEHICLES_TEXT,
      icon: 'icon-truck-dump',
      companyWide: false,
      navRoute: '/admin/trucks',
    },
    {
      key: 'feature-flag-vehicle-types',
      name: VEHICLE_TYPES_TEXT,
      icon: 'icon-view-list',
      navRoute: '/admin/vehicletypes',
      companyWide: false,
    },
  ],
};

export const categoriesForDocsGuides = [
  {
    name: 'GeoTrax Web',
    navRoute: '/geotrax/docs/guide/web',
    sub: true,
  },
  {
    name: 'GeoTrax Mobile',
    navRoute: '/geotrax/docs/guide/mobile',
    sub: true,
  },
];

export const categoriesForDocsRelease = [
  {
    name: 'Releases',
    icon: 'icon-launch',
    navRoute: '/geotrax/docs/releases',
    sub: false,
  },
];

export const categoriesForTruckTraxGeoDocsGuides = [
  {
    name: 'TruckTrax Geo',
    navRoute: '/geotrax/docs/guide/trucktraxgeo',
    sub: true,
  },
];

export const categoriesForTrainingVideos = [
  {
    name: 'GeoTrax Web',
    navRoute: '/geotrax/docs/videos/web',
    sub: true,
  },
  {
    name: 'GeoTrax Mobile',
    navRoute: '/geotrax/docs/videos/mobile',
    sub: true,
  },
];

export const categoriesForScaleTraxGuides = [
  {
    name: 'ScaleTrax Web',
    navRoute: '/scaletrax/docs/guide/web',
    sub: true,
  },
];

export const categoriesForScaleTraxRelease = [
  {
    name: 'Releases',
    icon: 'icon-launch',
    navRoute: '/scaletrax/docs/releases',
    sub: false,
  },
];

export const categoriesForScaleTraxTrainingVideos = [
  {
    name: 'ScaleTrax Web',
    navRoute: '/scaletrax/docs/videos/web',
    sub: true,
  },
];

export const adminNavItems = [
  {
    key: 'admin-nav-item',
    label: 'Admin',
    icon: 'settings',
    navRoute: '/admin',
    dataTest: 'Admin',
  },
];

export const geotraxNavItems: NavItem[] = [
  {
    key: 'geotrax-region-map-item',
    label: 'Region Map',
    icon: 'map',
    navRoute: '/geotrax/map',
    dataTest: 'RegionMap',
  },
  {
    key: 'geotrax-orders',
    label: 'Orders',
    icon: 'orders',
    navRoute: '/geotrax/orders',
    dataTest: 'Orders',
  },
  {
    key: 'geotrax-docs',
    label: 'Docs',
    icon: 'docs',
    navRoute: '/geotrax/docs',
    dataTest: 'Docs',
  },
  {
    key: 'geotrax-route-history',
    label: 'Route History',
    icon: 'route-search',
    navRoute: '/geotrax/routeHistory',
    dataTest: 'RouteHistory',
  },
  {
    key: 'geotrax-incoming-materials',
    label: 'Incoming Materials',
    icon: 'incoming-materials',
    navRoute: '/geotrax/incomingMaterials',
    dataTest: 'IncomingMaterials',
  },
];

export const scaleTraxNavItems = [
  {
    label: 'ScaleTrax',
    icon: 'tickets',
    navRoute: '/scaletrax/ticketing',
    dataTest: 'scaletrax',
  },
  {
    label: 'Search Tickets',
    icon: 'search',
    navRoute: '/scaletrax/scaletraxticketslist',
    dataTest: 'searchtickets',
  },
  {
    label: 'Docs',
    icon: 'docs',
    navRoute: '/scaletrax/docs',
    dataTest: 'Docs',
  },
];

export const ordersLinks = [
  {
    key: 'orders-links',
    name: ORDERS_HEADER,
    icon: 'icon-list',
    navRoute: '/geotrax/orders/all',
  },
  {
    key: 'orders-notifications',
    name: 'Notifications Feed',
    icon: 'icon-notifications',
    navRoute: '/geotrax/orders/notifications',
  },
];
